const withTimeout = async (promise, ms, label) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const pickFirstString = (value) => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const v of value) {
      const s = pickFirstString(v);
      if (s) return s;
    }
  }
  if (value && typeof value === 'object') {
    for (const k of ['definition', 'text', 'example']) {
      const s = pickFirstString(value[k]);
      if (s) return s;
    }
  }
  return null;
};

/**
 * Fetch a short English definition for a word.
 *
 * Uses dictionaryapi.dev (free). If it fails, returns a safe fallback.
 */
export const fetchEnglishDefinition = async (word, { timeoutMs = 6000 } = {}) => {
  const w = String(word || '').trim().toLowerCase();
  if (!w) {
    const err = new Error('Missing word');
    err.code = 'INVALID_WORD';
    throw err;
  }

  const url = `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(w)}`;
  const res = await withTimeout(fetch(url), timeoutMs, 'dictionary lookup');

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok || !Array.isArray(data) || !data.length) {
    const err = new Error('No definition found.');
    err.code = 'NO_DEFINITION';
    throw err;
  }

  const first = data[0] || {};
  const phonetic =
    typeof first?.phonetic === 'string' ? first.phonetic : null;

  const meanings = Array.isArray(first?.meanings) ? first.meanings : [];
  const firstMeaning = meanings[0] || {};
  const partOfSpeech =
    typeof firstMeaning?.partOfSpeech === 'string' ? firstMeaning.partOfSpeech : null;

  const definitions = Array.isArray(firstMeaning?.definitions)
    ? firstMeaning.definitions
    : [];
  const firstDefObj = definitions[0] || {};

  const definition =
    (typeof firstDefObj?.definition === 'string' && firstDefObj.definition.trim()) ||
    pickFirstString(firstMeaning) ||
    pickFirstString(first) ||
    null;

  const example =
    (typeof firstDefObj?.example === 'string' && firstDefObj.example.trim()) ||
    null;

  if (!definition) {
    const err = new Error('No definition found.');
    err.code = 'NO_DEFINITION';
    throw err;
  }

  return {
    word: w,
    phonetic,
    partOfSpeech,
    definition,
    example,
    sourceUrl:
      Array.isArray(first?.sourceUrls) && typeof first.sourceUrls[0] === 'string'
        ? first.sourceUrls[0]
        : null,
  };
};

